import React from 'react';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  Popup,
  VStack,
} from '@adc/parallax-component-library';
import QrCode, { QrCodeProps } from './QrCode';
import i18n from '../../utilities/i18n';

const QR_CODE_SIZE = 300;

type QrCodePopupProps = QrCodeProps & {
  error?: boolean;
  loading?: boolean;
};

/**
 * A popup component that displays a QR code for a study that can be scanned by the mobile app.
 * The popup is triggered by a button click and is automatically hidden when clicked outside.
 * @param {QrCodeProps} props - The properties for the QR code popup component.
 * @returns {React.JSX.Element}
 */
export function QrCodePopup({
  studyIdentifier,
  siteIdentifier,
  subjectIdentifier,
  authorizationCode,
  studyKey,
  siteKey,
  subjectKey,
  uploadFrequency,
  error = false,
  loading = false,
}: QrCodePopupProps) {
  return (
    <Popup
      placement="bottom-end"
      content={
        <VStack
          alignSelf="center"
          width={QR_CODE_SIZE}
          minHeight={QR_CODE_SIZE}
          justifyContent="center"
          alignItems="center"
        >
          {error ? (
            <Highlight
              title={i18n.t('QrCode.error.title')}
              body={i18n.t('QrCode.error.description')}
              iconStart={
                <IconBannersWarning
                  color="$neutral.0"
                  accentColor="$support.danger.default"
                />
              }
              testID="qr-code-error-highlight"
              variant="danger"
            />
          ) : null}
          {!error && loading ? (
            <LoadingSpinner testID="qr-code-loading" />
          ) : null}

          {!loading && !error ? (
            <QrCode
              studyIdentifier={studyIdentifier}
              siteIdentifier={siteIdentifier}
              subjectIdentifier={subjectIdentifier}
              authorizationCode={authorizationCode}
              studyKey={studyKey}
              siteKey={siteKey}
              subjectKey={subjectKey}
              uploadFrequency={uploadFrequency}
            />
          ) : null}
        </VStack>
      }
      contentPadding={true}
      testID="qr-code-popup"
    >
      <Button
        variant="secondary"
        size="medium"
        label={i18n.t('QrCode.view')}
        onPress={() => {}}
        testID={'qr-code-popup-button'}
        alignStart
      />
    </Popup>
  );
}
